import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Input } from '../components/ui/input';
// V13-editable-profile
import { 
  User, 
  Mail, 
  Phone, 
  CreditCard,
  LogOut,
  ChevronRight,
  Truck,
  Shield,
  Bell,
  HelpCircle,
  Scale,
  Building2,
  Crown
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { 
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../components/ui/alert-dialog';
import { toast } from 'sonner';
import { Capacitor } from '@capacitor/core';

const ContactInfoBlock = ({ user }) => {
  const [editingPhone, setEditingPhone] = useState(false);
  const [phone, setPhone] = useState(user?.phone || '');
  const [savingPhone, setSavingPhone] = useState(false);
  const [license, setLicense] = useState(user?.license_number || '');
  const [savingLic, setSavingLic] = useState(false);
  const [editingLic, setEditingLic] = useState(false);

  const API = process.env.REACT_APP_BACKEND_URL;
  const authHeader = () => ({ headers: { Authorization: 'Bearer ' + localStorage.getItem('token') } });

  const savePhone = async () => {
    setSavingPhone(true);
    try {
      await axios.patch(API + '/api/auth/me/profile', { phone }, authHeader());
      toast.success('Telephone mis a jour');
      setEditingPhone(false);
      setTimeout(() => window.location.reload(), 500);
    } catch (e) { toast.error('Erreur : ' + (e?.response?.data?.detail || e.message)); }
    setSavingPhone(false);
  };

  const saveLicense = async () => {
    setSavingLic(true);
    try {
      await axios.patch(API + '/api/auth/me/profile', { license_number: license }, authHeader());
      toast.success('Permis mis a jour');
      setEditingLic(false);
      setTimeout(() => window.location.reload(), 500);
    } catch (e) { toast.error('Erreur : ' + (e?.response?.data?.detail || e.message)); }
    setSavingLic(false);
  };

  const removeLicense = async () => {
    if (!window.confirm('Retirer votre numero de permis du profil ?')) return;
    setSavingLic(true);
    try {
      await axios.patch(API + '/api/auth/me/profile', { remove_license: true }, authHeader());
      toast.success('Permis retire');
      setTimeout(() => window.location.reload(), 500);
    } catch (e) { toast.error('Erreur : ' + (e?.response?.data?.detail || e.message)); }
    setSavingLic(false);
  };

  return (
    <div className="bg-slate-900 border border-slate-800 rounded-xl p-4 space-y-4" data-testid="contact-info">
      <div className="flex items-center gap-4">
        <div className="w-12 h-12 rounded-lg bg-slate-800 flex items-center justify-center">
          <Mail className="text-slate-400" size={20} />
        </div>
        <div className="flex-1">
          <p className="text-xs text-slate-500 uppercase font-semibold">Email</p>
          <p className="text-white font-medium">{user?.email}</p>
        </div>
      </div>

      <div className="flex items-start gap-4">
        <div className="w-12 h-12 rounded-lg bg-slate-800 flex items-center justify-center shrink-0">
          <Phone className="text-slate-400" size={20} />
        </div>
        <div className="flex-1">
          <p className="text-xs text-slate-500 uppercase font-semibold mb-1">Telephone</p>
          {editingPhone ? (
            <div className="flex gap-2">
              <Input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+1 514 555 1234" className="bg-slate-800 text-white" data-testid="input-edit-phone" />
              <Button size="sm" onClick={savePhone} disabled={savingPhone} data-testid="btn-save-phone">Enregistrer</Button>
              <Button size="sm" variant="ghost" onClick={() => { setEditingPhone(false); setPhone(user?.phone || ''); }}>Annuler</Button>
            </div>
          ) : (
            <div className="flex items-center justify-between">
              <p className="text-white font-medium">{user?.phone || <span className="text-slate-500 italic">Non renseigne</span>}</p>
              <Button size="sm" variant="outline" onClick={() => setEditingPhone(true)} data-testid="btn-edit-phone">Modifier</Button>
            </div>
          )}
        </div>
      </div>

      <div className="flex items-start gap-4">
        <div className="w-12 h-12 rounded-lg bg-slate-800 flex items-center justify-center shrink-0">
          <CreditCard className="text-slate-400" size={20} />
        </div>
        <div className="flex-1">
          <p className="text-xs text-slate-500 uppercase font-semibold mb-1">N Permis (facultatif)</p>
          {editingLic ? (
            <div className="flex gap-2">
              <Input value={license} onChange={(e) => setLicense(e.target.value)} placeholder="T1234-567890-01" className="bg-slate-800 text-white" data-testid="input-edit-license" />
              <Button size="sm" onClick={saveLicense} disabled={savingLic} data-testid="btn-save-license">Enregistrer</Button>
              <Button size="sm" variant="ghost" onClick={() => { setEditingLic(false); setLicense(user?.license_number || ''); }}>Annuler</Button>
            </div>
          ) : (
            <div className="flex items-center justify-between flex-wrap gap-2">
              <p className="text-white font-medium">{user?.license_number || <span className="text-slate-500 italic">Non renseigne</span>}</p>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={() => setEditingLic(true)} data-testid="btn-edit-license">
                  {user?.license_number ? 'Modifier' : 'Ajouter'}
                </Button>
                {user?.license_number && (
                  <Button size="sm" variant="destructive" onClick={removeLicense} disabled={savingLic} data-testid="btn-remove-license">Retirer</Button>
                )}
              </div>
            </div>
          )}
          <p className="text-xs text-slate-500 mt-1">Aucun lien ELD. Donnees chiffrees.</p>
        </div>
      </div>
    </div>
  );
};

const PreferencesBlock = () => {
  // V14-docs-toggle : preference locale pour masquer widget Documents utiles
  const [hideDocsWidget, setHideDocsWidget] = useState(
    typeof window !== 'undefined' && localStorage.getItem('hide_docs_widget') === '1'
  );
  const toggle = () => {
    const next = !hideDocsWidget;
    setHideDocsWidget(next);
    localStorage.setItem('hide_docs_widget', next ? '1' : '0');
    toast.success(next ? 'Widget Documents masque' : 'Widget Documents affiche');
  };
  return (
    <div className="bg-slate-900 border border-slate-800 rounded-xl p-4" data-testid="preferences-block">
      <p className="text-xs text-slate-500 uppercase font-semibold mb-3">Preferences accueil</p>
      <div className="flex items-center justify-between">
        <div className="flex-1 pr-4">
          <p className="text-white font-medium">Afficher "Documents utiles"</p>
          <p className="text-xs text-slate-400 mt-1">Masquer si vous utilisez un ELD ou n avez pas besoin de ce rappel.</p>
        </div>
        <div className="flex items-center gap-3">
          <span className={`text-xs font-bold uppercase ${hideDocsWidget ? 'text-slate-500' : 'text-amber-500'}`}>
            {hideDocsWidget ? 'Masque' : 'Affiche'}
          </span>
          <button
          onClick={toggle}
          data-testid="toggle-docs-widget"
          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors \${hideDocsWidget ? 'bg-slate-700' : 'bg-amber-500'}`}
          aria-pressed={!hideDocsWidget}
        >
          <span className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform \${hideDocsWidget ? 'translate-x-1' : 'translate-x-5'}`} />
        </button>
        </div>
      </div>
    </div>
  );
};

export const ProfilePage = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  // Le menu "Découvrir ProPilote / Forfaits" est caché sur les apps natives
  // (iOS + Android) pour respecter Apple Guideline 3.1.1 et par cohérence
  // B2B — les tarifs restent visibles uniquement sur le Web pour les prospects.
  const isNative = Capacitor.isNativePlatform();

  const handleLogout = () => {
    logout();
    toast.success('Déconnexion réussie');
    navigate('/login');
  };

  const menuItems = [
    {
      id: 'enterprises',
      icon: Building2,
      label: 'Mes entreprises',
      description: 'Connexions et invitations',
      path: '/driver/enterprises'
    },
    {
      id: 'notifications',
      icon: Bell,
      label: 'Notifications',
      description: 'Gérer les alertes',
      path: '/notifications'
    },
    {
      id: 'security',
      icon: Shield,
      label: 'Sécurité',
      description: 'Mot de passe',
      path: '/security'
    },
    {
      id: 'help',
      icon: HelpCircle,
      label: 'Aide',
      description: 'Support et FAQ',
      path: '/help'
    },
    {
      id: 'legal',
      icon: Scale,
      label: 'Mentions légales',
      description: 'Avis important',
      path: '/legal'
    },
    // Cache "Découvrir ProPilote" sur les apps natives (iOS + Android)
    // — visible uniquement sur le Web (Apple Guideline 3.1.1)
    ...(isNative ? [] : [{
      id: 'propilote',
      icon: Crown,
      label: 'Découvrir ProPilote',
      description: 'Forfaits et fonctionnalités',
      path: '/presentation'
    }])
  ];

  return (
    <div className="page-wrapper" data-testid="profile-page">
      {/* Header with profile info */}
      <div 
        className="relative h-48 bg-cover bg-center"
        style={{ 
          backgroundImage: `url('https://images.unsplash.com/photo-1652081439602-b917d33f794b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzh8MHwxfHNlYXJjaHwzfHxzZW1pJTIwdHJ1Y2slMjBoaWdod2F5JTIwc3Vuc2V0fGVufDB8fHx8MTc3Mjg4OTQ3MXww&ixlib=rb-4.1.0&q=85')`
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-t from-[hsl(222,47%,3%)] to-transparent" />
        <div className="absolute bottom-0 left-0 right-0 p-4">
          <div className="flex items-end gap-4">
            <div className="w-20 h-20 rounded-xl bg-amber-500 flex items-center justify-center shadow-lg">
              <span className="text-3xl font-bold text-black">
                {user?.first_name?.[0]}{user?.last_name?.[0]}
              </span>
            </div>
            <div className="flex-1">
              <h1 className="text-2xl font-bold text-white">
                {user?.first_name} {user?.last_name}
              </h1>
              <p className="text-amber-500 font-semibold uppercase text-sm tracking-wider">
                Chauffeur
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="p-4 space-y-4">
        <ContactInfoBlock user={user} />
        <PreferencesBlock />
        {/* Menu Items */}
        <div className="bg-slate-900 border border-slate-800 rounded-xl overflow-hidden" data-testid="profile-menu">
          {menuItems.map((item, index) => (
            <button
              key={item.id}
              onClick={() => navigate(item.path)}
              className={`w-full p-4 flex items-center gap-4 text-left transition-colors hover:bg-slate-800 ${index !== 0 ? 'border-t border-slate-800' : ''}`}
              data-testid={`menu-${item.id}`}
            >
              <div className="w-12 h-12 rounded-lg bg-slate-800 flex items-center justify-center">
                <item.icon className="text-slate-400" size={20} />
              </div>
              <div className="flex-1">
                <p className="text-white font-medium">{item.label}</p>
                <p className="text-slate-500 text-sm">{item.description}</p>
              </div>
              <ChevronRight className="text-slate-600" size={20} />
            </button>
          ))}
        </div>

        {/* Logout Button */}
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button
              variant="outline"
              className="w-full h-14 border-red-900 text-red-400 hover:bg-red-900/20 font-bold uppercase tracking-wide"
              data-testid="logout-button"
            >
              <LogOut className="mr-2" size={20} />
              Déconnexion
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent className="bg-slate-900 border-slate-800">
            <AlertDialogHeader>
              <AlertDialogTitle className="text-white">Confirmer la déconnexion</AlertDialogTitle>
              <AlertDialogDescription className="text-slate-400">
                Êtes-vous sûr de vouloir vous déconnecter de l'application?
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel className="bg-slate-800 border-slate-700 text-white hover:bg-slate-700">
                Annuler
              </AlertDialogCancel>
              <AlertDialogAction 
                onClick={handleLogout}
                className="bg-red-600 hover:bg-red-700 text-white"
                data-testid="confirm-logout"
              >
                Déconnexion
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* App Version */}
        <div className="pt-8 pb-2 text-center">
          <button type="button" onClick={() => navigate('/account/delete')} className="text-xs text-slate-600 hover:text-red-400 underline underline-offset-4 transition-colors" data-testid="delete-account-link">
            Gerer mon compte (suppression)
          </button>
        </div>
        <p className="text-center text-slate-600 text-sm pt-4">
          TruckDriverPro v1.0.0
        </p>
      </div>
    </div>
  );
};

export default ProfilePage;
